import { useEffect, useRef } from 'react';

import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import { helper } from '@myrealtrip/frontend-weblog';
import { media } from '@myrealtrip/modash';
import { Popup, Text } from '@myrealtrip/web-ui';

import Show from '~components/Show';
import { WeblogEventName, WeblogPageCategory, WeblogScreenName } from '~constants/weblog';
import useToast from '~hooks/useToast';
import useTravelerOption from '~hooks/pointToPointTicket/useTravelerOption';
import { DEFAULT_TRAVELER_AGE, MAX_REGISTERABLE_TRAVELERS } from '~constants/pointToPointTicket';

import type { SearchConditionPopupLocation } from '~types/pointToPointTicket';

import TravelerCountStepper from './SearchType/Traveler/TravelerCountStepper';
import TravelerAgeList from './SearchType/Traveler/TravelerAgeList';
import TravelerAgeCalculator from './SearchType/Traveler/TravelerAgeCalculator';
import TravelerNotificationList from './SearchType/Traveler/TravelerNotificationList';
import TravelerAgeDescriptionTable from './SearchType/Traveler/TravelerAgeDescriptionTable';

const { sendClickLog, sendLayerViewLog } = helper;

interface Props {
  isOpen: boolean;
  currentTravelers: number[];
  popupLocation?: SearchConditionPopupLocation;
  onApply: (ages: number[], closeAfterApply?: boolean) => void;
  onClose: () => void;
}

const TravelerDropdown = ({ isOpen, currentTravelers, popupLocation, onApply, onClose }: Props) => {
  const theme = useTheme();
  const optionListRef = useRef<HTMLDivElement>(null);
  const toast = useToast({ position: 'bottom-center' });

  const {
    travelers,
    setTravelers,
    selectedTraveler,
    setSelectedTraveler,
    viewSelectedTravelerAge,
    setViewSelectedTravelerAge,
    handleApplyTravelers,
    handleIncreaseTraveler,
    handleDecreaseTraveler,
  } = useTravelerOption({ currentTravelers, onApply });

  const onIncreaseTraveler = () => {
    if (travelers.length === MAX_REGISTERABLE_TRAVELERS) {
      toast.success({
        id: 'max-travelers',
        message: '최대 9명까지 예약 가능합니다.',
      });
      return;
    }

    handleIncreaseTraveler(DEFAULT_TRAVELER_AGE);
    handleApplyTravelers({
      updatedTravelers: [...travelers, DEFAULT_TRAVELER_AGE],
      closeAfterApply: false,
    });
  };

  const onDecreaseTraveler = () => {
    handleDecreaseTraveler();
    handleApplyTravelers({
      updatedTravelers: travelers.slice(0, travelers.length - 1),
      closeAfterApply: false,
    });
  };

  const updateTravelers = (travelers: number[]) => {
    setTravelers(travelers);
    setSelectedTraveler(null);
    setViewSelectedTravelerAge(false);
    handleApplyTravelers({ updatedTravelers: travelers, closeAfterApply: false });
  };

  const handleClickOutside = () => {
    sendClickLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.APPLY_PAX,
      itemKind: 'button',
      itemName: '인원 적용',
      pax: travelers.length,
      age: travelers.map((age) => age.toString()).join(','),
    });

    onClose();
  };

  useEffect(() => {
    if (isOpen && travelers.length === 0) {
      handleApplyTravelers({
        updatedTravelers: [],
        closeAfterApply: false,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    sendLayerViewLog({
      pageCategory: WeblogPageCategory.EUROPE_TRAIN_TICKET_MAIN,
      screenName: WeblogScreenName.EUROPE_TRAIN_TICKET_MAIN,
      eventName: WeblogEventName.PAX,
      itemName: '탑승 인원',
    });
  }, [isOpen]);

  const hasNoTravelers = travelers.length === 0;

  return (
    <CustomPopup
      popupLocation={popupLocation}
      visible={isOpen}
      onOutsideClick={handleClickOutside}
      insideElements={[optionListRef]}
    >
      <Container>
        <StepperContainer>
          <Text typography="paragraphBold15" color={theme.colors.gray.s1000}>
            인원 선택
          </Text>
          <TravelerCountStepper
            currentValue={travelers.length}
            onIncrease={onIncreaseTraveler}
            onDecrease={onDecreaseTraveler}
            min={1}
            max={MAX_REGISTERABLE_TRAVELERS}
          />
        </StepperContainer>
        <Show when={hasNoTravelers}>
          <div style={{ display: 'flex', gap: '4px', justifyContent: 'end', paddingRight: '20px' }}>
            <Text typography="captionNormal15" color={theme.colors.red.s500}>
              탑승인원을 선택해주세요
            </Text>
          </div>
        </Show>
        <Show when={travelers.length > 0}>
          <TravelerAgeList
            ref={optionListRef}
            travelers={travelers}
            selectedTraveler={selectedTraveler}
            viewSelectedTravelerAge={viewSelectedTravelerAge}
            exposureAgeSelectView={() => setViewSelectedTravelerAge(true)}
            updateTravelers={updateTravelers}
            updateSelectedTraveler={setSelectedTraveler}
          />
        </Show>
        <div style={{ marginTop: '10px' }} />
        <TravelerAgeDescriptionTable />
        <TravelerAgeCalculator />
        <TravelerNotificationList />
      </Container>
    </CustomPopup>
  );
};

const CustomPopup = styled(Popup)<{ popupLocation?: SearchConditionPopupLocation }>`
  width: 390px;
  border-radius: 12px;

  ${({ popupLocation }) => popupLocation?.css && css(popupLocation.css as any)}
  z-index: ${({ theme }) => theme.zIndex.modal};
`;

const Container = styled.div`
  scrollbar-width: none;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const StepperContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;

  ${media.minMobile} {
    padding: 10px 20px;
  }
`;

export default TravelerDropdown;
